import { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";
import { getProductById } from "@/data/menu";
import { getComboById } from "@/data/combos";
import { computeLinePrice } from "@/lib/format";
import { scopedKey, readJSON, writeJSON } from "@/lib/storage";
import { trackGuestEvent } from "@/lib/guestEvents";

const CartContext = createContext(null);
const storageKey = () => scopedKey("cart");

let lineCounter = Date.now();
const nextLineId = () => `line_${lineCounter++}`;

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => readJSON(storageKey(), []));

  useEffect(() => {
    writeJSON(storageKey(), items);
  }, [items]);

  // line = { lineId, productId, quantity, selections, note }
  const addItem = useCallback((line) => {
    if (!line.isCombo) trackGuestEvent("cart.add", { productId: line.productId, quantity: line.quantity });
    setItems((prev) => [...prev, { ...line, lineId: nextLineId() }]);
  }, []);

  const addItems = useCallback((lines) => {
    setItems((prev) => [...prev, ...lines.map((line) => ({ ...line, lineId: nextLineId() }))]);
  }, []);

  const updateLine = useCallback((lineId, patch) => {
    setItems((prev) => prev.map((l) => (l.lineId === lineId ? { ...l, ...patch } : l)));
  }, []);

  const setQuantity = useCallback((lineId, quantity) => {
    setItems((prev) =>
      quantity <= 0
        ? prev.filter((l) => l.lineId !== lineId)
        : prev.map((l) => (l.lineId === lineId ? { ...l, quantity } : l))
    );
  }, []);

  const removeItem = useCallback((lineId) => {
    setItems((prev) => prev.filter((l) => l.lineId !== lineId));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const { count, subtotal, detailed } = useMemo(() => {
    let count = 0;
    let subtotal = 0;
    const detailed = items.map((line) => {
      if (line.isCombo) {
        const combo = getComboById(line.comboId);
        const unitPrice = combo ? combo.price : 0;
        const lineTotal = unitPrice * line.quantity;
        count += line.quantity;
        subtotal += lineTotal;
        return { ...line, product: combo ? { name: combo.name, image: combo.image, isCombo: true } : null, unitPrice, lineTotal };
      }
      const product = getProductById(line.productId);
      const unitPrice = product ? computeLinePrice(product, line.selections || {}) : 0;
      const lineTotal = unitPrice * line.quantity;
      count += line.quantity;
      subtotal += lineTotal;
      return { ...line, product, unitPrice, lineTotal };
    });
    return { count, subtotal, detailed };
  }, [items]);

  const value = useMemo(
    () => ({ items, detailed, count, subtotal, addItem, addItems, updateLine, setQuantity, removeItem, clearCart }),
    [items, detailed, count, subtotal, addItem, addItems, updateLine, setQuantity, removeItem, clearCart]
  );
  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
